/**
 * VdiExpandedPanel
 * Main expanded-row component for the VDI Transaction DataTable.
 *
 * Matches the original expanded-row content exactly — only the UI is redesigned:
 *   1. VdiStepperFlow   – request workflow stepper (same AlertCommonStepper)
 *   2. VdiErrorsSection – error records (only when errors exist)
 *   3. VdiPayloadSection – payload JSON/XML viewer with copy
 */
import React from 'react';
import { useSelector } from 'react-redux';
import { FiAlertCircle } from 'react-icons/fi';
import { NoDataPlate, SpinnerBlock } from '@/components/organism/TableExpand/primitives';
import { ORGANISM_PROPS } from '@/constant/global/global.constant';
import { VDI_EXPANDED_FAILURE_MESSAGES } from '@/pages/VdiTransaction/constant';
import type { IVDITransactionProps } from '@/utils/types/vdiTransaction';
import { getVdiTransactionList } from '@/store/selectors/vdiTransaction.selector';
import { tid } from '@/utils/testId';
import { VDI_TRANSACTION_TEST_IDS as TID } from '@/constant/testIds/vdiTransaction.testIds';
import { VdiStepperFlow } from './VdiStepperFlow';
import { VdiErrorsSection } from './VdiErrorsSection';
import { VdiPayloadSection } from './VdiPayloadSection';

export interface VdiExpandedPanelProps {
  /** The table row data (lightweight – from the list) */
  data: IVDITransactionProps;
  /** Table row index from DataTable expanded-row renderer */
  rowIndex?: number;
  /** True while the single-item fetch is in flight */
  loading?: boolean;
  /** True when provider has the config object */
  hasProviderConfig?: boolean;
  /** True when transaction-by-id request failed */
  detailFailed?: boolean;
  /** True when provider-by-id request failed */
  providerFailed?: boolean;
}

export const VdiExpandedPanel = ({
  data,
  rowIndex,
  loading = false,
  hasProviderConfig,
  detailFailed = false,
  providerFailed = false,
}: VdiExpandedPanelProps) => {
  const storeState = useSelector(getVdiTransactionList) as any;
  const vditransaction = storeState?.vditransaction;
  const providerState = useSelector((state: any) => state.provider);
  const vdiProvider = providerState?.provider;
  const providerReady = vdiProvider?.providerId === data?.providerId;

  // Only show detailed content once the store has the correct record
  const isMatching =
    vditransaction?.vdiTransactionId === data?.vdiTransactionId;
  const errors = isMatching ? (vditransaction?.errors ?? []) : [];
  const payloadMessage = isMatching ? vditransaction?.message : null;

  // Show spinner while row details/provider context are still being resolved.
  const providerLoading = !!hasProviderConfig && !!data?.providerId && !providerReady;

  if (loading) {
    return <SpinnerBlock message={ORGANISM_PROPS.VDI_EXPANDED.LOADING_MSG} />;
  }

  if (detailFailed || providerFailed) {
    return (
      <NoDataPlate
        message={
          detailFailed
            ? VDI_EXPANDED_FAILURE_MESSAGES.DETAIL
            : VDI_EXPANDED_FAILURE_MESSAGES.PROVIDER
        }
        icon={<FiAlertCircle className="h-8 w-8" />}
      />
    );
  }

  return (
    <div
      data-testid={tid(TID.EXPANDED_PANEL)}
      className="px-2 py-3 space-y-3 transition-opacity duration-200 opacity-100"
    >
      {/* 1 ── Workflow stepper */}
      <VdiStepperFlow
        data={data}
        rowIndex={rowIndex}
        providersListArray={providerReady ? [vdiProvider] : []}
        loading={loading}
        hasConfig={hasProviderConfig}
        testId={tid(TID.EXPANDED_STEPPER)}
      />

      {/* 2 ── Errors (only when present) */}
      {isMatching && errors.length > 0 && (
        <VdiErrorsSection errors={errors} testId={tid(TID.EXPANDED_ERRORS)} />
      )}

      {/* 3 ── Payload viewer */}
      {isMatching && (
        <VdiPayloadSection
          row={vditransaction as unknown as IVDITransactionProps}
          payloadMessage={payloadMessage}
          testId={tid(TID.EXPANDED_PAYLOAD)}
        />
      )}
    </div>
  );
};
